import { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from "@mui/material";
import { useApi } from "../../hooks/useApi";
import { BillingProfileSummary } from "../../types/billing";

interface Props {
  open: boolean;
  companyId: string;
  onClose: () => void;
  onCreated: (billingProfile: BillingProfileSummary) => Promise<void> | void;
}

export default function AddBillingProfileDialog({ open, companyId, onClose, onCreated }: Props) {
  const { post } = useApi();
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    setName("");
    setError(null);
  };

  const handleClose = () => {
    if (saving) return;
    reset();
    onClose();
  };

  const handleDialogClose = (_event: object, reason: string) => {
    if (reason === "backdropClick") return;
    handleClose();
  };

  const handleSubmit = async () => {
    const trimmed = name.trim();
    if (!trimmed || saving) return;
    setSaving(true);
    setError(null);
    try {
      const created = await post<BillingProfileSummary>(`/api/companies/${companyId}/billing-profiles`, { name: trimmed });
      reset();
      await onCreated(created);
    } catch (err: any) {
      setError(err.message || "Failed to add billing profile");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleDialogClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add billing profile</DialogTitle>
      <DialogContent>
        <Alert severity="warning" sx={{ mb: 2 }}>
          With more than one billing profile, alerts that don't match a brand are not billed. Before adding
          one, make sure:
          <Box component="ul" sx={{ my: 1, pl: 2.5, listStyle: "disc" }}>
            <li>payment processor accounts are linked to the correct brand</li>
            <li>enrolments have the correct brand</li>
          </Box>
          A billing profile is used only once at least one brand is assigned to it.
        </Alert>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <TextField
          autoFocus
          fullWidth
          label="Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") handleSubmit(); }}
          disabled={saving}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={saving || !name.trim()}>
          Add billing profile
        </Button>
      </DialogActions>
    </Dialog>
  );
}
